import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import Button from "./ui/Button";
import { getApiBaseUrl } from "../utils/ApiClient";
import {
  isApiDown,
  pingApi,
  reportApiUp,
  subscribeApiStatus,
} from "../utils/apiStatus";

// How often to check whether the API is back while this screen is up.
const RETRY_MS = 15000;

const useOnline = () => {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
};

/* Covers the app while the API cannot be reached (server down, deploying,
   or the device is offline) instead of leaving pages half-loaded. It checks
   in the background and gets out of the way once the API answers again; the
   page underneath is kept, so nothing typed is lost. */
const MaintenanceScreen = () => {
  const apiDown = useSyncExternalStore(subscribeApiStatus, isApiDown);
  const online = useOnline();
  const [checking, setChecking] = useState(false);

  const checkNow = useCallback(async () => {
    setChecking(true);
    try {
      if (await pingApi(getApiBaseUrl())) reportApiUp();
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!apiDown) return undefined;
    const timer = setInterval(checkNow, RETRY_MS);
    return () => clearInterval(timer);
  }, [apiDown, checkNow]);

  // Coming back online is the most likely moment for the API to answer.
  useEffect(() => {
    if (apiDown && online) checkNow();
  }, [apiDown, online, checkNow]);

  if (!apiDown) return null;

  const title = online ? "We'll be right back" : "You're offline";
  const message = online
    ? "SplitPOS is under maintenance or temporarily unavailable. Your data is safe — we'll reconnect automatically as soon as the service is back."
    : "Check your internet connection. We'll reconnect automatically once you're back online.";

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="maintenance-title"
      aria-describedby="maintenance-message"
      className="tw-fixed tw-inset-0 tw-z-[2000] tw-flex tw-items-center tw-justify-center tw-bg-black/60 tw-p-4"
    >
      <div className="tw-w-full tw-max-w-[440px] tw-rounded-brand tw-bg-surface tw-p-6 tw-text-center tw-text-ink tw-shadow-card sm:tw-p-8">
        <span
          aria-hidden="true"
          className="tw-mx-auto tw-mb-4 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-brand-red tw-text-2xl tw-text-white"
        >
          <i className={`fa ${online ? "fa-wrench" : "fa-wifi"}`} />
        </span>
        <h2
          id="maintenance-title"
          className="tw-mb-2 tw-w-auto tw-font-header tw-text-22 tw-font-semibold tw-text-brand-navy"
        >
          {title}
        </h2>
        <p
          id="maintenance-message"
          className="tw-mb-5 tw-text-sm tw-text-ink-secondary"
        >
          {message}
        </p>
        <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-3">
          <Button loading={checking} onClick={checkNow}>
            Try again
          </Button>
          <Button variant="neutral" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceScreen;
